import type { CharacterId, CourtEventId, CourtEventState } from './types';

export interface CourtEventBeat {
  atMs: number;
  speakerId: CharacterId;
  label: string;
  line: string;
  intensity: 'low' | 'medium' | 'high';
}

export interface CourtEventScript {
  title: string;
  beats: CourtEventBeat[];
}

export const COURT_EVENT_SCRIPTS: Record<CourtEventId, CourtEventScript> = {
  'lyra-confession': {
    title: 'THE CLIENT BREAKS SILENCE',
    beats: [
      {
        atMs: 0,
        speakerId: 'lyra',
        label: 'Lyra Vale · under oath',
        line: 'Yes. I entered the archive. I went to recover my sister’s memory—not to kill Orin.',
        intensity: 'medium',
      },
      {
        atMs: 3100,
        speakerId: 'judge',
        label: 'Justice Voss',
        line: 'The admission is entered. The purpose remains contested.',
        intensity: 'low',
      },
    ],
  },
  'silas-outburst': {
    title: 'THE COURIER ERUPTS',
    beats: [
      {
        atMs: 0,
        speakerId: 'silas',
        label: 'Silas Venn · shouting',
        line: 'I carried a sealed case! I did not open it, and I will not hang for Halden’s private cipher!',
        intensity: 'high',
      },
      {
        atMs: 3300,
        speakerId: 'judge',
        label: 'Justice Voss',
        line: 'Lower your voice, Mister Venn. Your lie is already loud enough.',
        intensity: 'medium',
      },
    ],
  },
  'iona-breaks': {
    title: 'CERTAINTY COLLAPSES',
    beats: [
      {
        atMs: 0,
        speakerId: 'iona',
        label: 'Iona Mercer · shaken',
        line: 'Seven minutes… I certified the request, not the calibration. The archive clock was never court time.',
        intensity: 'medium',
      },
      {
        atMs: 3300,
        speakerId: 'judge',
        label: 'Justice Voss',
        line: 'Then the state’s timeline has no lawful foundation.',
        intensity: 'low',
      },
    ],
  },
  'rook-rage': {
    title: 'THE PREFECT LOSES CONTROL',
    beats: [
      {
        atMs: 0,
        speakerId: 'rook',
        label: 'Rook Halden · enraged',
        line: 'You have no authority to dissect a prefect credential in open court!',
        intensity: 'high',
      },
      {
        atMs: 3000,
        speakerId: 'judge',
        label: 'Justice Voss',
        line: 'In my courtroom, truth outranks your credential.',
        intensity: 'medium',
      },
    ],
  },
  'rook-lunge': {
    title: 'ORDER IN THE COURT',
    beats: [
      {
        atMs: 0,
        speakerId: 'rook',
        label: 'Rook Halden · witness stand',
        line: 'Stop. You do not know what was inside those memories!',
        intensity: 'high',
      },
      {
        atMs: 2200,
        speakerId: 'rook',
        label: 'Rook Halden · advancing',
        line: 'Close the record—now!',
        intensity: 'high',
      },
      {
        atMs: 3900,
        speakerId: 'bailiff',
        label: 'Marshal Arden',
        line: 'Back from counsel. Hands where the court can see them.',
        intensity: 'high',
      },
      {
        atMs: 5600,
        speakerId: 'judge',
        label: 'Justice Voss · gavel',
        line: 'Order! Prefect Halden is remanded. Counsel will finish the record.',
        intensity: 'high',
      },
    ],
  },
};

export function getCourtEventBeat(event: CourtEventState): CourtEventBeat {
  const script = COURT_EVENT_SCRIPTS[event.id];
  return [...script.beats].reverse().find((beat) => event.elapsedMs >= beat.atMs) ?? script.beats[0];
}

export function getCourtEventProgress(event: CourtEventState): number {
  return Math.min(1, event.elapsedMs / event.durationMs);
}
